import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock, Calendar } from "lucide-react";
import Layout from "@/components/Layout";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { z } from "zod";

// Zod validation schema for contact form
const contactSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  
  phone: z.string()
    .trim()
    .min(10, "Phone number must be at least 10 digits")
    .max(20, "Phone number too long")
    .regex(/^[\d\s\-\(\)\+]+$/, "Phone number contains invalid characters"),
  
  message: z.string()
    .max(1000, "Message must be less than 1000 characters")
    .optional()
});

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate with Zod schema
    try {
      contactSchema.parse(formData);
      
      // In a real implementation, this would send to a backend
      toast({
        title: "Appointment Request Received!",
        description: "Diane will contact you within 24 hours to confirm your appointment.",
      });

      // Reset form
      setFormData({ name: "", email: "", phone: "", message: "" });
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.issues[0];
        toast({
          title: "Invalid Input",
          description: firstError.message,
          variant: "destructive",
        });
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold">Ready to Get Started?</h1>
            <p className="text-xl text-primary-foreground/90">
              Book your appointment today — it only takes a minute.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="p-8 border-2 hover:border-secondary/30 transition-all duration-300 shadow-xl bg-gradient-to-br from-card to-card/50">
              <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Schedule Your Appointment
              </h2>
              <p className="text-muted-foreground mb-6">Fill out the form below and we'll get back to you within 24 hours</p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="913-555-0123"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me a bit about your tax situation..."
                    rows={4}
                  />
                </div>

                <Button type="submit" size="lg" className="w-full">
                  Request Appointment
                </Button>

                <p className="text-sm text-muted-foreground text-center">
                  You'll receive a confirmation call or email within 24 hours
                </p>
              </form>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="p-6 border-2 border-secondary/20 bg-gradient-to-br from-card to-card/50 hover:shadow-xl transition-all duration-300">
                <h3 className="font-semibold text-xl mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Phone</p>
                      <a href="tel:913-406-7852" className="text-secondary hover:underline">
                        913-406-7852
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Email</p>
                      <a href="mailto:Diane@TinyTaxLady.com" className="text-secondary hover:underline break-all">
                        Diane@TinyTaxLady.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Address</p>
                      <p className="text-muted-foreground">
                        9330 Gillette St.<br />
                        Lenexa, KS 66215
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Hours</p>
                      <p className="text-muted-foreground">Monday - Friday<br />By Appointment</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-secondary/10 to-accent/10 border-2 border-secondary/30 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-lg">
                    <Calendar className="h-6 w-6 text-secondary flex-shrink-0" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Flexible Scheduling</h3>
                    <p className="text-muted-foreground">
                      I offer both in-person and virtual appointments to fit your schedule. Most appointments are available within a few days.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Map placeholder */}
              <Card className="overflow-hidden h-64 bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center border-2 border-primary/20 hover:shadow-xl transition-all duration-300 relative">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,166,153,0.1),transparent_70%)]"></div>
                <div className="text-center p-6 relative z-10">
                  <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-full mb-4">
                    <MapPin className="h-12 w-12 text-secondary" />
                  </div>
                  <p className="text-sm font-semibold text-foreground mb-1">Lenexa, Kansas</p>
                  <p className="text-xs text-muted-foreground">
                    Serving Lenexa, Overland Park, Shawnee, and Kansas City metro
                  </p>
                </div>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 border-2 border-accent/20 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-2xl"></div>
                <div className="relative z-10">
                  <p className="text-xl font-medium italic text-center mb-3 text-foreground">
                    "I look forward to helping you make tax time easy this year!"
                  </p>
                  <p className="text-center text-muted-foreground font-semibold">— Diane, The Tiny Tax Lady</p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
